/**
 * Клієнт API платформи (S9): загальні записи, метадані, пошук. Запис — з `Idempotency-Key`
 * дії людини (api-writes.md, розд. 1) і передумовою (`If-Match` або `base`). Помилки —
 * Problem Details: клієнт перекладає `code`, а не текст сервера.
 */
import type {
  AppMeta,
  DuplicateDismissalBody,
  DuplicateDismissalResult,
  DuplicatesCheckBody,
  DuplicatesCheckResult,
  EntityLayouts,
  FilterNode,
  HistoryEntry,
  MeResponse,
  PublicCompanyProfile,
  RecordCard,
  RecordQueryPage,
  RecordWriteResult,
  RevealResponse,
  SearchResult,
  ViewSort,
} from '@clientline/contracts';
import { SESSION_EXPIRED_CODE } from '../api/auth-codes.ts';
import { takeEarly } from '../api/client.ts';
import { parseRetryAfter, READ_ONLY_TEMPORARY, retryAfterOf } from '../api/temporary.ts';
import { withRuleRequired } from './rule-required.ts';

export { parseRetryAfter, retryAfterOf, SESSION_EXPIRED_CODE };

export class ApiProblem extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: Record<string, unknown> = {},
    /** `Retry-After` відповіді, секунд: коли повтор має сенс (UX-19). */
    readonly retryAfter?: number,
  ) {
    super(`${code} (HTTP ${status})`);
    this.name = 'ApiProblem';
  }
}

const MERGED_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Основний запис з відмови поглиненого (DAT-08; records-merge.md, «Старі адреси»):
 * `params.mergedInto` у `404` читання й `409 state_conflict` запису; `null` — звичайна відмова.
 */
export function mergedIntoOf(error: unknown): string | null {
  if (!(error instanceof ApiProblem)) return null;
  const params = error.body.params;
  const into =
    (typeof params === 'object' && params !== null
      ? (params as { mergedInto?: unknown }).mergedInto
      : undefined) ?? error.body.mergedInto;
  return typeof into === 'string' && MERGED_UUID.test(into) ? into : null;
}

/** UUIDv4 для ключа дії: crypto.randomUUID є в усіх цільових браузерах (secure context). */
export const newKey = (): string => crypto.randomUUID();

/**
 * Зміна, що отримала тимчасову відмову, сама чекає `Retry-After` і повторюється ОДИН раз з тим
 * самим `Idempotency-Key` (UX-18, UX-19), якщо чекати не довше за це. Довше — відмова йде
 * викликачу: людина бачить «повторіть через N с» і вирішує сама.
 */
export const AUTO_RETRY_MAX_S = 10;

/** Очікування перед автоматичним повтором зміни: оболонка показує його з таймером. */
export interface BusyWait {
  code: string;
  seconds: number;
  /** Коли повтор, `Date.now()`-мс. */
  until: number;
}

/**
 * Вид відповіді 401 (user-cabinet.md, розд. 10.1): `expired` — природний сплив сесії
 * (`session_expired`), `unauthenticated` — решта: відкликання, вимкнення людини, тіло
 * без коду чи з невідомим кодом (fail closed).
 */
export type UnauthorizedKind = 'expired' | 'unauthenticated';

const unauthorized = new Set<(kind: UnauthorizedKind) => void>();
const busyListeners = new Set<(wait: BusyWait) => (() => void) | undefined>();

/**
 * Підписка на будь-яку відповідь 401: сесії більше немає (вийшли, відкликано, строк минув).
 * Застосунок прибирає чернетки вкладки на обидва види (states.md, розд. 7, умова 2; умова 3
 * Security E3a-06b), а на `expired` ще й показує повторний вхід поверх сторінки
 * (`platform/relogin.ts`). Документ входу не підписується: там 401 — це невірний пароль.
 */
export function onUnauthorized(listener: (kind: UnauthorizedKind) => void): () => void {
  unauthorized.add(listener);
  return () => unauthorized.delete(listener);
}

function notifyUnauthorized(body: unknown): void {
  const kind: UnauthorizedKind =
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === SESSION_EXPIRED_CODE
      ? 'expired'
      : 'unauthenticated';
  for (const listener of unauthorized) listener(kind);
}

const networkFailures = new Set<() => void>();

/**
 * Запит не дійшов до сервера (мережева помилка `fetch`): факт для стану «немає зв'язку» (UX-29).
 * Слухає `platform/network.ts`, який перевіряє зв'язок окремим запитом; сам клієнт API стану мережі
 * не тримає — його бере й документ входу.
 */
export function onNetworkFailure(listener: () => void): () => void {
  networkFailures.add(listener);
  return () => networkFailures.delete(listener);
}

/** Зміни (не GET), що вже пішли на сервер і ще не мають відповіді: «Оновити» їх дочікується (UX-31). */
let writesInFlight = 0;
const writesSettled = new Set<() => void>();

/**
 * Дочекатися, доки зміни в дорозі отримають відповідь, але не довше `limitMs`: перезавантаження
 * посеред запису лишило б людину без відповіді, збережено чи ні.
 */
export function waitForWrites(limitMs: number): Promise<void> {
  if (writesInFlight === 0) return Promise.resolve();
  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer);
      writesSettled.delete(done);
      resolve();
    };
    const timer = setTimeout(done, limitMs);
    writesSettled.add(done);
  });
}

/**
 * Підписка на очікування перед автоматичним повтором зміни (`platform/busy.ts` показує таймер).
 * Слухач повертає функцію, яку викличемо, коли очікування скінчилось (повтор пішов чи скасовано).
 */
export function onBusyWait(listener: (wait: BusyWait) => (() => void) | undefined): () => void {
  busyListeners.add(listener);
  return () => busyListeners.delete(listener);
}

function abortError(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Aborted', 'AbortError');
}

async function waitBusy(code: string, seconds: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) throw abortError(signal);
  const wait: BusyWait = { code, seconds, until: Date.now() + seconds * 1000 };
  const ends = [...busyListeners].map((listener) => listener(wait));
  try {
    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(resolve, seconds * 1000);
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(abortError(signal));
        },
        { once: true },
      );
    });
  } finally {
    for (const end of ends) end?.();
  }
}

/**
 * Скільки пам'ятаємо власну зміну: відлуння в SSE приходить за мілісекунди, хвилина — із запасом на
 * обрив і відновлення потоку. Старші — геть при наступному додаванні.
 */
export const OWN_CHANGES_TTL_MS = 60_000;
/**
 * Запобіжник розміру поза віком: каскадне скасування дає сотні змін за раз, і їхні відлуння ще в
 * дорозі, тож межа — із запасом над будь-якою одиницею роботи.
 */
export const OWN_CHANGES_MAX = 5_000;

/**
 * Відлуння власних змін у SSE: `changeId`, які спричинила ця вкладка (api-writes.md, розд. 5).
 * Витіснення — за віком (`OWN_CHANGES_TTL_MS`), а не кількістю: інакше пакет на понад кілька сотень
 * змін витіснив би власні зміни раніше, ніж прийде їхнє відлуння.
 */
export const ownChanges = new (class OwnChanges extends Set<string> {
  /**
   * Коли вкладка отримала відповідь зміни (`performance.now()`, монотонний — не стрибає з
   * годинником системи): відлуння — `platform/events.ts`.
   */
  private stamps: Map<string, number> | undefined;
  override add(id: string): this {
    this.stamps ??= new Map();
    const now = performance.now();
    // Порядок множини — порядок міток: повторна зміна стає наймолодшою.
    super.delete(id);
    this.stamps.set(id, now);
    super.add(id);
    // Найстаріші — геть: множина живе всю сесію вкладки.
    for (const old of this) {
      const at = this.stamps.get(old) ?? Number.NEGATIVE_INFINITY;
      if (now - at <= OWN_CHANGES_TTL_MS && this.size <= OWN_CHANGES_MAX) break;
      this.delete(old);
    }
    return this;
  }
  override delete(id: string): boolean {
    this.stamps?.delete(id);
    return super.delete(id);
  }
  override clear(): void {
    this.stamps?.clear();
    super.clear();
  }
  /** Час відповіді власної зміни; `undefined` — зміна не наша. */
  at(id: string): number | undefined {
    return this.stamps?.get(id);
  }
})();

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function send(
  method: Method,
  url: string,
  headers: Record<string, string>,
  options: { body?: unknown; signal?: AbortSignal; cache?: RequestCache; keepalive?: boolean },
  first: boolean,
): Promise<Response> {
  try {
    // Ранній запит з public/boot.js (метадані першого екрана) — забрати один раз, а не слати вдруге.
    const early = method === 'GET' && first ? await takeEarly(url) : null;
    return (
      early ??
      (await fetch(url, {
        method,
        headers,
        credentials: 'same-origin',
        ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
        ...(options.signal ? { signal: options.signal } : {}),
        ...(options.cache ? { cache: options.cache } : {}),
        ...(options.keepalive ? { keepalive: true } : {}),
      }))
    );
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error;
    for (const listener of networkFailures) listener();
    throw new ApiProblem(0, 'network');
  }
}

/**
 * Тіло JSON. Зіпсоване тіло відмови — порожній об'єкт: код тоді `http_<статус>`, а 401 без коду —
 * `unauthenticated` (fail closed). Зіпсоване тіло успіху — помилка, як і раніше.
 */
async function jsonOf(res: Response): Promise<Record<string, unknown>> {
  if (res.ok) return (await res.json()) as Record<string, unknown>;
  try {
    const body: unknown = await res.json();
    return typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** Відмова у формі Problem Details → `ApiProblem` (код, тіло, `Retry-After`). */
function problemOf(method: Method, res: Response, body: Record<string, unknown>): ApiProblem {
  const code = String(body.code ?? `http_${res.status}`);
  // Зміна з відмовою «лише для читання» — без `retryAfter`: це звичайний збій, а не «зайнято».
  const readOnly = method !== 'GET' && READ_ONLY_TEMPORARY.has(code);
  return new ApiProblem(
    res.status,
    code,
    body,
    readOnly ? undefined : parseRetryAfter(res.headers.get('retry-after')),
  );
}

export async function api<T>(
  method: Method,
  url: string,
  options: {
    body?: unknown;
    /** Передумова запису: версія запису (число) чи ревізія стану адмінки (рядок). */
    ifMatch?: number | string;
    key?: string;
    signal?: AbortSignal;
    /**
     * Скасовує лише очікування `Retry-After` перед автоматичним повтором, а не запит у дорозі:
     * запит, що вже пішов, доходить до кінця. Для дій, де обрив відповіді шкодить (step-up ротує
     * сесію в `Set-Cookie`: обрив до заголовків — 401 і стерті чернетки, рев'ю Security-2 #289).
     */
    retrySignal?: AbortSignal;
    /**
     * Запит доходить, навіть якщо сторінку покидають (`fetch` з `keepalive`): дзвінок `tel:`
     * відкриває софтфон, і браузер може вивантажити вкладку (calls.md, розд. 4, «Бюджет»).
     */
    keepalive?: boolean;
    /**
     * Читання методом `POST` — пакети лічильників подань і дошки (`view-count.ts`,
     * `board-data.ts`): тимчасову відмову тут не повторюємо й таймера «зайнято» зміни не
     * показуємо — тихо повторює смуга лічильників (`counts-lane.ts`), як `retryRead` для `GET`.
     */
    read?: boolean;
  } = {},
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  // Ключ — один на виклик: автоматичний повтор нижче йде з тими самими заголовками.
  if (method !== 'GET') headers['idempotency-key'] = options.key ?? newKey();
  if (options.ifMatch !== undefined) headers['if-match'] = `"${options.ifMatch}"`;
  if (method === 'GET' || options.read) return request<T>(method, url, headers, options);
  writesInFlight++;
  try {
    return await request<T>(method, url, headers, options);
  } finally {
    writesInFlight--;
    if (writesInFlight === 0) for (const done of [...writesSettled]) done();
  }
}

async function request<T>(
  method: Method,
  url: string,
  headers: Record<string, string>,
  options: {
    body?: unknown;
    signal?: AbortSignal;
    retrySignal?: AbortSignal;
    keepalive?: boolean;
    read?: boolean;
  },
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    const res = await send(method, url, headers, options, attempt === 0);
    if (res.status === 204) return undefined as T;
    const type = res.headers.get('content-type') ?? '';
    // SPA-fallback edge віддає index.html на невідомий шлях — це «не знайдено», а не дані.
    if (res.ok && !type.includes('json')) throw new ApiProblem(404, 'not_found');
    const body = type.includes('json') ? await jsonOf(res) : {};
    // Вид 401 — за тілом (`session_expired` чи ні), тож слухачі дізнаються після розбору.
    if (res.status === 401) notifyUnauthorized(body);
    if (!res.ok) {
      const problem = problemOf(method, res, body);
      // Читання повторює TanStack Query (`retryRead`), зміну — тут: лише раз і лише недовго.
      const wait = retryAfterOf(problem);
      if (
        method === 'GET' ||
        options.read ||
        attempt > 0 ||
        wait === null ||
        wait > AUTO_RETRY_MAX_S
      ) {
        throw problem;
      }
      await waitBusy(problem.code, wait, options.retrySignal ?? options.signal);
      continue;
    }
    const changeId = (body as { changeId?: unknown }).changeId;
    if (typeof changeId === 'string') ownChanges.add(changeId);
    return body as T;
  }
}

/** Результат читання з передумовою: `same` — `304`, у клієнта вже той самий вміст. */
export type Revalidated<T> = { status: 'same' } | { status: 'fresh'; body: T; etag: string | null };

/**
 * Читання з ревалідацією за `ETag`, який клієнт тримає в пам'яті (повне читання довідника,
 * references.md, розд. 3): `If-None-Match` → `304` без тіла. `cache: 'no-store'` — відповідь не
 * лягає в HTTP-кеш браузера: той живе на диску й після виходу, а дані довідника мають зникати разом
 * із сесією (вимога Security S2 #336, SEC-008, SEC-024). Повтори — як у читань (TanStack Query).
 */
export async function revalidate<T>(
  url: string,
  options: { etag?: string | null; signal?: AbortSignal } = {},
): Promise<Revalidated<T>> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.etag) headers['if-none-match'] = options.etag;
  const res = await send(
    'GET',
    url,
    headers,
    { cache: 'no-store', ...(options.signal ? { signal: options.signal } : {}) },
    false,
  );
  if (res.status === 304) return { status: 'same' };
  const type = res.headers.get('content-type') ?? '';
  if (res.ok && !type.includes('json')) throw new ApiProblem(404, 'not_found');
  const body = type.includes('json') ? await jsonOf(res) : {};
  if (res.status === 401) notifyUnauthorized(body);
  if (!res.ok) throw problemOf('GET', res, body);
  return { status: 'fresh', body: body as T, etag: res.headers.get('etag') };
}

/** Скільки разів повторювати читання (UX-18): мережа, 5xx, тимчасова відмова з `Retry-After`. */
export const READ_RETRIES = 3;
/** Тихий повтор читання (UX-19): лише якщо сервер просить чекати не довше; інакше — стан помилки. */
export const READ_QUIET_WAIT_S = 10;

const statusOf = (error: unknown): number | null => {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : null;
};

/**
 * `retry` TanStack Query для читань: 4xx і помилки коду не повторюємо (повтор їх не виправить),
 * мережу (0) і 5xx — до трьох разів, тимчасову відмову — якщо `Retry-After` ≤ 10 с.
 */
export function retryRead(failureCount: number, error: unknown): boolean {
  if (failureCount >= READ_RETRIES) return false;
  const wait = retryAfterOf(error);
  if (wait !== null) return wait <= READ_QUIET_WAIT_S;
  const status = statusOf(error);
  return status !== null && (status === 0 || status >= 500);
}

/** `retryDelay`: `Retry-After`, інакше 1 → 2 → 4 с; з розкидом, щоб вкладки не били хвилею. */
export function readRetryDelay(failureCount: number, error: unknown): number {
  const wait = retryAfterOf(error);
  if (wait !== null) return wait * 1000 + Math.random() * 250;
  return Math.min(1000 * 2 ** failureCount, 8000) * (0.75 + Math.random() * 0.5);
}

export const platformKeys = {
  meta: ['p', 'meta'] as const,
  me: ['p', 'me'] as const,
  company: ['p', 'company'] as const,
  layouts: (entity: string) => ['p', 'layouts', entity] as const,
  list: (entity: string, view: string) => ['p', 'list', entity, view] as const,
  lists: (entity: string) => ['p', 'list', entity] as const,
  record: (entity: string, id: string) => ['p', 'record', entity, id] as const,
  recordCheck: (entity: string, id: string) => ['p', 'record-check', entity, id] as const,
  history: (entity: string, id: string) => ['p', 'history', entity, id] as const,
  /** Панелі пов'язаного запису (REC-12): префікс — усі зв'язки запису, події запису його скидають. */
  related: (entity: string, id: string) => ['p', 'related', entity, id] as const,
  /** Лінія клієнта запису (Г1-W1), усі розміри сторінки: префікс ключа `timeline/query.ts`. */
  timeline: (entity: string, id: string) => ['p', 'timeline', entity, id] as const,
  /** Довідники цілком (references.md): лише кеш у пам'яті, скидається сигналом SSE і 401. */
  references: ['p', 'ref'] as const,
  reference: (entity: string) => ['p', 'ref', entity] as const,
  /**
   * Свої канали сповіщень (NTF-26), матриця профілю. Під `p` — вихід скидає кеш; не під ключем
   * центру — дії нагадування його не скидають.
   */
  notificationSettings: ['p', 'notifications', 'settings'] as const,
  pushSubscriptions: ['p', 'notifications', 'push-subscriptions'] as const,
};

/**
 * Поточний користувач (`GET /api/v1/me`, IAM; схема — `@clientline/contracts`, `me.ts`): оболонці
 * потрібне ім'я, «Моєму профілю» — ще email, `version` (передумова `PATCH /me`) і `emailChange`.
 */
export type Me = MeResponse;

export const fetchMe = (signal?: AbortSignal) =>
  api<MeResponse>('GET', '/api/v1/me', { ...(signal ? { signal } : {}) });

/**
 * Профіль компанії для оболонки (`GET /api/v1/settings/company`, будь-яка людина після входу):
 * мова, пояс і перший день тижня компанії — типові для тих, хто своїх не обрав (PRF-03, PRF-04).
 */
export const fetchCompanyProfile = (signal?: AbortSignal) =>
  api<PublicCompanyProfile>('GET', '/api/v1/settings/company', { ...(signal ? { signal } : {}) });

/** Метадані з позначкою обов'язковості з правил сутності (`withRuleRequired`, ім'я контакту). */
export const fetchMeta = (signal?: AbortSignal) =>
  api<AppMeta>('GET', '/api/v1/meta', { ...(signal ? { signal } : {}) }).then(withRuleRequired);
export const fetchLayouts = (entity: string, signal?: AbortSignal) =>
  api<EntityLayouts>('GET', `/api/v1/meta/entities/${entity}/layouts`, {
    ...(signal ? { signal } : {}),
  });
/**
 * Сторінка подання (`POST …/query`): `limit` — розмір порції списку (50, легкий режим 25; LST-14).
 * `viewId` — лише позначка для звіту «Повільні подання» (LST-16). Картка дозавантажує знімок для `J`
 * тим самим запитом (list-context.ts).
 */
export const fetchQueryPage = (
  entity: string,
  body: {
    filter?: FilterNode | undefined;
    /** Немає — лише з `q`: сервер сортує за релевантністю (SRC-15). */
    sort?: ViewSort | undefined;
    fields?: readonly string[] | undefined;
    viewId?: string | undefined;
    q?: string | undefined;
  },
  limit: number,
  signal?: AbortSignal,
  cursor?: string,
) =>
  api<RecordQueryPage>('POST', `/api/v1/records/${entity}/query`, {
    body: {
      ...(body.filter ? { filter: body.filter } : {}),
      ...(body.sort ? { sort: [body.sort] } : {}),
      ...(body.q ? { q: body.q } : {}),
      ...(body.fields && body.fields.length > 0 ? { fields: body.fields } : {}),
      limit,
      ...(body.viewId ? { viewId: body.viewId } : {}),
      ...(cursor ? { cursor } : {}),
    },
    ...(signal ? { signal } : {}),
  });

/**
 * Картка запису. `include` — читання поведінок, що приходять разом з карткою (`card.includes`,
 * behaviors.md, розд. 6): назви з `/meta` сутності (`cardInclude`), бо невідома назва дає `400`.
 */
export const fetchCard = (
  entity: string,
  id: string,
  signal?: AbortSignal,
  include: readonly string[] = [],
) =>
  api<RecordCard>(
    'GET',
    `/api/v1/records/${entity}/${id}${include.length > 0 ? `?include=${include.join(',')}` : ''}`,
    { ...(signal ? { signal } : {}) },
  );
/**
 * Повне значення замаскованого поля (SEC-074): кожен виклик — подія журналу безпеки й одиниця межі
 * на годину (`429 reveal_limit`). Значення живе лише в стані компонента, що його попросив: у кеш
 * запитів (`platformKeys`) воно не кладеться.
 */
export const revealField = (entity: string, id: string, field: string) =>
  api<RevealResponse>('POST', `/api/v1/records/${entity}/${id}/fields/${field}/reveal`);
/**
 * Перевірка відкритої картки (AG-69, п. в): `304`, якщо запис видимий, версія та сама й права глядача
 * ті самі (`accessTag`). Це не перегляд: ні полів, ні рядка журналу. Без `include`: його дані тег не
 * описує.
 */
export const checkCard = (
  entity: string,
  id: string,
  card: Pick<RecordCard, 'version' | 'accessTag'>,
  signal?: AbortSignal,
) =>
  revalidate<RecordCard>(`/api/v1/records/${entity}/${id}`, {
    etag: `"${card.version}.${card.accessTag}"`,
    ...(signal ? { signal } : {}),
  });
export const fetchHistory = (entity: string, id: string, signal?: AbortSignal) =>
  api<{ items: HistoryEntry[] }>('GET', `/api/v1/records/${entity}/${id}/history`, {
    ...(signal ? { signal } : {}),
  });
/**
 * Глобальний пошук. Текст запиту — у тілі `POST`, а не в адресі (SEC-110): шукають і за телефоном
 * та поштою, а URL лягає в історію, проксі й звіти про помилки. `limit` (записів на групу) і
 * `entity` (одна сутність) — теж у тілі; немає — сервер бере свої (related-search-api.md, розд. 1).
 */
export const search = (
  q: string,
  signal?: AbortSignal,
  options: { limit?: number; entity?: string; reveal?: false } = {},
) =>
  api<SearchResult>('POST', '/api/v1/search', {
    body: { q, ...options },
    ...(signal ? { signal } : {}),
  });
/**
 * Створення. `duplicateReason` — ключ причини з переліку правила дублів `require_reason`, коли
 * людина свідомо зберігає попри дубль (uniqueness.md, розд. 5); без неї — поле не надсилаємо.
 */
export const createRecord = (
  entity: string,
  values: Record<string, unknown>,
  key: string,
  duplicateReason?: string,
) =>
  api<RecordWriteResult>('POST', `/api/v1/records/${entity}`, {
    body: { values, ...(duplicateReason ? { duplicateReason } : {}) },
    key,
  });
export const patchRecord = (
  entity: string,
  id: string,
  values: Record<string, unknown>,
  base: Record<string, unknown>,
  key: string,
  duplicateReason?: string,
) =>
  api<RecordWriteResult>('PATCH', `/api/v1/records/${entity}/${id}`, {
    body: { values, base, ...(duplicateReason ? { duplicateReason } : {}) },
    key,
  });

/**
 * Підказка дублів під час введення (DAT-06; uniqueness.md, розд. 5): значення полів правил — у тілі
 * `POST`, а не в адресі (телефон і пошта — ПД, SEC-110). Безпечне читання: нічого не змінює.
 */
export const checkDuplicates = (entity: string, body: DuplicatesCheckBody, signal?: AbortSignal) =>
  api<DuplicatesCheckResult>('POST', `/api/v1/records/${entity}/duplicates`, {
    body,
    ...(signal ? { signal } : {}),
  });

/** «Не дубль» пари записів за правилом і скасування позначки; повтор — `changed: false`. */
export const dismissDuplicate = (entity: string, body: DuplicateDismissalBody, revoke = false) =>
  api<DuplicateDismissalResult>(
    'POST',
    `/api/v1/records/${entity}/duplicates/dismissals${revoke ? '/revoke' : ''}`,
    { body },
  );
